import { useState, type DragEvent, type KeyboardEvent } from 'react';
import { Typography } from '@arco-design/web-react';

const { Text } = Typography;

export type SortableSelectedItem = { id: string; label: string };

export function moveSelectedItem(ids: string[], from: number, to: number): string[] {
  if (from < 0 || to < 0 || from >= ids.length || to >= ids.length || from === to) return ids;
  const next = [...ids];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

export function dropIndex(from: number, target: number, after: boolean): number {
  const insertion = target + (after ? 1 : 0);
  return from < insertion ? insertion - 1 : insertion;
}

export default function SortableSelectedItems(props: {
  items: SortableSelectedItem[];
  onReorder: (ids: string[]) => void;
}) {
  const [dragId, setDragId] = useState<string>();
  const [dropTarget, setDropTarget] = useState<{ id: string; after: boolean }>();

  const afterMidpoint = (event: DragEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return event.clientY >= bounds.top + bounds.height / 2;
  };

  const ids = props.items.map(item => item.id);
  const clearDrag = () => {
    setDragId(undefined);
    setDropTarget(undefined);
  };

  return <>
    <strong>已选常用</strong>
    <Text type="secondary">拖动条目调整首页顺序；也可用方向键调整</Text>
    <div className="portal-common-config__sortable" role="list" aria-label="已选常用顺序">
      {props.items.map((item, index) => {
        const dropClass = dropTarget?.id === item.id
          ? dropTarget.after ? 'portal-common-config__selected-item--drop-after'
            : 'portal-common-config__selected-item--drop-before' : '';
        const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
          event.preventDefault();
          const next = moveSelectedItem(ids, index, index + (event.key === 'ArrowUp' ? -1 : 1));
          if (next !== ids) props.onReorder(next);
        };
        return <div className={[
          'portal-common-config__selected-item',
          'portal-common-config__selected-item--draggable',
          dragId === item.id ? 'portal-common-config__selected-item--dragging' : '',
          dropClass,
        ].filter(Boolean).join(' ')} key={item.id} role="listitem" tabIndex={0} draggable
          aria-label={`${item.label}，拖动或按上下方向键调整顺序`}
          onKeyDown={onKeyDown}
          onDragStart={event => {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', item.id);
            setDragId(item.id);
          }}
          onDragOver={event => {
            if (!dragId || dragId === item.id) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            setDropTarget({ id: item.id, after: afterMidpoint(event) });
          }}
          onDrop={event => {
            event.preventDefault();
            if (!dragId || dragId === item.id) return clearDrag();
            const from = ids.indexOf(dragId);
            const to = dropIndex(from, index, afterMidpoint(event));
            props.onReorder(moveSelectedItem(ids, from, to));
            clearDrag();
          }}
          onDragEnd={clearDrag}>
          <strong>{item.label}</strong>
          <span className="portal-common-config__drag-handle" aria-hidden="true">⠿</span>
        </div>;
      })}
    </div>
  </>;
}
